/**
 * WorkRow — THE one list row for every unit of work in this app.
 *
 * Graduated from /styleguide/work-rows on 2026-07-22 once the grammar was
 * settled there (docs/design/WORK-ROW-SYSTEM.md). The prototype page now imports
 * THIS module, so the styleguide demonstrates the shipping component instead of
 * a copy of it — the only arrangement under which a styleguide cannot drift from
 * the thing it documents.
 *
 * ── Why one row ──────────────────────────────────────────────────────────────
 * A "unit of work in a list" is currently re-declared ~41 times. An issue alone
 * renders through six different rows; a goal through two; an attention signal
 * through four. The drift is not stylistic noise — sibling routes (`/issues` vs
 * `/projects/<slug>/issues`) show the SAME task with different padding, a
 * different status glyph, and one of them striped.
 *
 * ── The unification ─────────────────────────────────────────────────────────
 * The two best existing rows already agree on their leading grammar:
 *
 *   GoalLedgerRow   [kind tile] [health dot] title ······ [meter] [lane chip]
 *   LifecycleRow    [priority]  [status ring] title ····· [pills] [owner] [date] [actions]
 *
 * Two leading slots, a title, a right-aligned metadata band. WorkRow is their
 * UNION, not a third row: slot 1 = SIGNIFIER (what kind of thing / how urgent),
 * slot 2 = STATE (where it is in its lifecycle, or how bad it is), then the
 * title, then an optional METER (goals/projects/checklists — what LifecycleRow
 * lacks) and the pills/owner/date/actions cluster (what GoalLedgerRow lacks).
 *
 * Every slot is OPTIONAL and every slot is FIXED WIDTH when present, so the
 * columns form straight vertical rules down a list even when the rows carry
 * different payloads — a goal row and an issue row can sit in the same list.
 *
 * ── The three rules ─────────────────────────────────────────────────────────
 * 1. FLAT. Dividers only, never zebra striping (Matt, 2026-07-22). The
 *    prototype carried a `demoZebra` prop only to A/B what was being deleted;
 *    that decision was made, so it did not graduate.
 * 2. ONE RHYTHM. Padding comes from `--row-py` (globals.css @theme, 10px), the
 *    same token RowShell uses — `density="dense"` overrides the token, it does
 *    not add a sixth hard-coded padding tier.
 * 3. ONE GUTTER. `gutter` = px-4, the crowned card gutter that RowShell and
 *    `SectionTitle variant="card"` already share. (LifecycleRow's px-3 is the
 *    4px outlier being retired.)
 * 4. NO UNLABELED MARK. Every non-text thing in the row — the kind glyph, the
 *    priority bars, the state ring/dot, the meter, the tag chip, the avatar,
 *    the abbreviated date, the hover actions — carries a real tooltip saying
 *    what it means (Matt, 2026-07-22). This is a TYPE rule, not a convention:
 *    where the label can't be derived it is REQUIRED on the model, so an
 *    unexplained mark fails to compile rather than shipping. Native `title=`
 *    is not a tooltip — it's unstyled, ~1s late, and invisible on touch; the
 *    house rule (IconTooltip, 2026-07-16) is the Radix bubble everywhere.
 */

import * as React from "react";
import { MaybeObjectLink } from "./object-link";
import { Badge } from "../badge";
import { Button } from "../button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../tooltip";
import { cn } from "../utils";
import { type Intent } from "../tokens";
import { CircleAlert } from "../icons";
import { HealthBar } from "./health-bar";
import { IntentBadge } from "./status-badge";
import { StatusRing, ringLabel, type RingState } from "./status-ring";
import { PriorityIcon } from "./excellent/priority-icon";
import {
  PRIORITY_LABEL,
  STATUS_LABEL,
  type TaskPriority,
  type TaskStatus,
} from "../types/projects";

type Glyph = React.ComponentType<React.SVGProps<SVGSVGElement>>;

/**
 * The row's tooltip. One `TooltipProvider` sits at the ROW (see `WorkRow`) —
 * it renders no DOM, so wrapping every row costs nothing and a standalone row
 * works without an ambient provider, the same self-contained guarantee
 * `IconTooltip` gives an icon button.
 *
 * `asChild` merges onto the slot span, so a tooltip never adds a box to a
 * fixed-width column and never nests a <button> inside the row's <Link>.
 */
function Tip({ label, children }: { label: React.ReactNode; children: React.ReactElement }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

/** Sentence case for any label, without touching the data: `first-letter`
 *  uppercases only the first character, so "design system" reads "Design
 *  system" and "excellent-site" doesn't become "Excellent-Site" the way a
 *  `capitalize` transform would. */
const SENTENCE_CASE = "first-letter:uppercase";

/** A tooltip that names the DIMENSION before the value — "Project · micro",
 *  "Due · Jul 23". The abbreviation in the row is only legible if the bubble
 *  says what kind of thing it abbreviates. */
function Dimension({ of, children }: { of: string; children: React.ReactNode }) {
  return (
    <span className="type-subtext">
      <span className="text-muted-foreground">{of}</span> · {children}
    </span>
  );
}

// ── The model ────────────────────────────────────────────────────────────────

/**
 * The kind mark — slot 1.
 *
 * SHAPE SAYS WHAT, COLOR SAYS HOW IT'S DOING. The signifier is a bare, muted
 * glyph in every case: the glyph's SHAPE identifies the artifact (a target is a
 * strategic goal, a folder is a project, a pull-request mark is a review), and
 * color is reserved entirely for the state dot beside it. This is the Review
 * queue treatment (Matt, 2026-07-22: "icon then the status"), promoted to the
 * rule.
 *
 * Two earlier signifiers are gone because each opened a second color channel
 * that competed with state: the tinted goal TILE (a colored block saying
 * "goal") and the project identity DOT (a colored disc saying "micro"). A row
 * with a violet block, an amber dot, and a neutral bar is three color systems
 * arguing; now there is one.
 *
 * `priority` is the single exception, and only because it is a real signal
 * rather than a label — the bars encode urgency, which is information the glyph
 * slot would otherwise waste on repeating "this is an issue" forty times.
 *
 * `label` is REQUIRED on a glyph: the whole premise of the slot is that a shape
 * names the artifact, and a shape nobody can name is decoration. (Priority
 * derives its own from `PRIORITY_LABEL`, so it can't drift from the picker.)
 */
export type WorkSignifier =
  | { as: "glyph"; icon: Glyph; label: string }
  | { as: "priority"; priority: TaskPriority };

/**
 * WHERE IT IS IN ITS LIFE. One vocabulary, every kind.
 *
 * ── The fill IS the state ───────────────────────────────────────────────────
 * A dashed ring, a half-filled ring, a three-quarter ring, a filled check, a
 * struck ring. `StatusRing` already spoke this language for issues; the plain
 * colored dot every other kind used spoke nothing — it was color-only, so it
 * carried nothing for a colorblind reader and nothing at a glance until you had
 * memorized the palette. Now a goal, a project and an issue at the same point in
 * their lives look the same, because they are the same fact.
 *
 * ── Why health had to leave this slot ───────────────────────────────────────
 * The obvious move was to map our five intents onto the five fills. It doesn't
 * work: the fill vocabulary is a LIFECYCLE language (how far through its life),
 * and intent is a HEALTH grade (at risk / broken). Mapping health onto fills
 * would make fill mean progress *and* health — exactly the conflation the color
 * model just pulled apart.
 *
 * So health left. "At risk" and "broken" are not states of a thing; they are
 * reasons a person is needed, which is the attention mark's whole job.
 *
 * ── …and the one refinement that let `failed` in ────────────────────────────
 * A machine-driven task can DIE (Matt, 2026-07-22: "some tasks will be machine
 * driven so they could have errors… we need a red/alert version"), and a red
 * ring looked at first like the rule breaking. It isn't — the rule was never
 * "the ring is never red", it was:
 *
 *     the ring never GRADES work in flight. It may name how work ENDED.
 *
 * There are four ways to end, and they get the four honest tones: `done` well
 * (green) · `cancelled` deliberately (grey) · `failed` badly (red) ·
 * `duplicate` because it was never its own thing (grey). Nothing here grades a
 * LIVE goal or a running task — that is still exclusively the mark. So the
 * intents still split by "who is needed", with the ring's red confined to a
 * verdict on something already over:
 *
 *     needs nobody  (`neutral` · `info` · `success`)  → tones the RING
 *     needs somebody (`warning` · `danger`)           → tones the MARK
 *                                                     (+ `danger` on a DEAD ring)
 *
 * ── Two variants, one look ──────────────────────────────────────────────────
 * `status` is the task workflow, reading its shape and label from the shared
 * registries so an issue row says what the board and the picker say. `phase` is
 * the reduction every other kind maps onto. Both accept the two derived states,
 * because a goal's pursuit can fail exactly like a task's run can.
 */
export type WorkPhase =
  | "notStarted"
  | "active"
  | "nearlyDone"
  | "done"
  | "cancelled"
  | "failed"
  | "duplicate";

/** Lifecycle order — the legend on the prototype reads this, so the page can't
 *  drift from the component. The three endings sit together at the tail. */
export const PHASE_ORDER: WorkPhase[] = [
  "notStarted",
  "active",
  "nearlyDone",
  "done",
  "cancelled",
  "failed",
  "duplicate",
];

export type WorkState =
  | { as: "status"; status: RingState; label?: string }
  | { as: "phase"; phase: WorkPhase; label?: string };

/** Phase → the ring shape it borrows. Reuses `StatusRing` rather than drawing a
 *  second set of rings, so the two variants can never drift apart. */
export const PHASE_RING: Record<WorkPhase, RingState> = {
  notStarted: "backlog",
  active: "in_progress",
  nearlyDone: "review",
  done: "done",
  cancelled: "cancelled",
  failed: "failed",
  duplicate: "duplicate",
};

/**
 * Phase → tone. `warning` is still absent and always will be — "at risk" is a
 * grade on something still alive, which is the mark's job. `danger` appears
 * exactly once, on `failed`, which is a verdict on something already over.
 *
 * This is also where the prototype visibly parts company with the app: through
 * `TASK_STATUS_INTENT`, `in_progress` tones AMBER, so a goal's "Active" ring
 * would arrive claiming the goal is at risk. The `intent` override on
 * `StatusRing` (additive, default unchanged) is what lets this row be right
 * without re-coloring every board column in the app. That fix is D7.
 */
export const PHASE_INTENT: Record<WorkPhase, Intent> = {
  notStarted: "neutral",
  active: "info",
  nearlyDone: "info",
  done: "success",
  cancelled: "neutral",
  failed: "danger",
  duplicate: "neutral",
};

export const PHASE_LABEL: Record<WorkPhase, string> = {
  notStarted: "Not started",
  active: "Active",
  nearlyDone: "Nearly done",
  done: "Complete",
  cancelled: "Cancelled",
  failed: "Failed",
  duplicate: "Duplicate",
};

/**
 * Did this thing end BADLY? The row asks so it can suppress the attention mark:
 * on a failed row "a person is needed" is already maximally true and the reason
 * is already in the ring's own bubble, so a second red glyph 8px away would
 * spend the row's whole color budget saying one thing twice. One rule, in one
 * place, instead of eight adapters each remembering not to set `attention`.
 */
export function isFailed(state?: WorkState): boolean {
  if (!state) return false;
  return state.as === "status" ? state.status === "failed" : state.phase === "failed";
}

/** The trailing progress readout — linked issues, key results, checklist items,
 *  milestone steps. ONE meter in ONE neutral color: forty rows of intent-colored
 *  segments read as confetti, and the tally beside the bar already says it.
 *  `caption` is required — a bar with no units is a mood. */
/**
 * The ROW formats the tally, not the adapter. An adapter that hands over a
 * pre-baked `"24/48"` string is an adapter that can format it differently from
 * the next one — which is how `24/48`, `24 / 48` and `24 of 48` all ended up
 * shipping. Give it the two numbers; the row renders them one way, with the
 * slash spaced off the digits so the pair reads as two numbers rather than one
 * run of glyphs *(Matt, 2026-07-22)*.
 */
export interface WorkMeter {
  value: number;
  max: number;
  /** What is being counted, as a noun phrase: "linked issues done". */
  caption: string;
}

/** A context chip — project, section, lane, role, stage. `hint` names the
 *  DIMENSION ("Project"), since the chip only shows the value and truncates. */
export interface WorkPill {
  icon?: Glyph;
  label: string;
  hint: string;
  accent?: string;
}

export interface WorkAction {
  icon: Glyph;
  label: string;
  onClick?: () => void;
}

/** The avatar shows initials; the tooltip has to say who that is — and in what
 *  capacity, because the same circle is the assignee on an issue and the
 *  shipper on a review, which are not the same claim about a person. */
export interface WorkOwner {
  initials: string;
  name: string;
  /** The relationship: "Assignee", "Shipper", "Driver". Defaults to "Owner". */
  role?: string;
  color?: string;
}

/**
 * Three states, not two. `undefined` means this KIND has no owner concept — a
 * milestone has nobody to assign — and the cell stays blank. `"unassigned"`
 * means this kind DOES take an owner and doesn't have one, which is a fact
 * worth showing: an unassigned issue is a real gap, and a silent empty cell
 * reads identically to "this kind never has one" *(Matt, 2026-07-22)*.
 */
export type WorkOwnerSlot = WorkOwner | "unassigned";

/**
 * Something needs a person, for a reason that isn't where the thing is in its
 * life: a goal with no finish line, a review that's been sitting on you, a
 * high-severity finding. ONE shape, ONE position on every kind — the reason
 * lives in the tooltip.
 *
 * TWO tones, not one. When health moved out of the state slot it brought its
 * grades with it: a Medium finding and a High one are both "a person is
 * needed", and flattening them would throw away the only thing that ranks an
 * audit list. `warning` is at risk, `danger` is broken — the same two words the
 * color model already defines, now living in the one channel that is about you.
 * Still one shape: the tone says how loud, never what.
 */
export interface WorkAttention {
  /** The reason. On a BLOCKED row this is the ACTION — "Needs your verify". */
  label: string;
  /** `warning` (default) = at risk · `danger` = broken. */
  level?: "warning" | "danger";
  /**
   * BLOCKED — the work has stopped and will not move until the reader acts.
   *
   * This is a different fact from "something is off", and it earned its own
   * treatment by failing in practice *(Matt, 2026-07-22: "right now we have a
   * red thing but that didn't catch 'needs your verify' from the tasks")*. Two
   * separate failures were behind that:
   *
   *   1. The mark was never SET on the rows that need it most. A task sitting in
   *      `review` is the most blocked thing in this app — it cannot become done
   *      until a different agent verifies it — and it drew no mark at all,
   *      because "in review" looked like a lifecycle state and got left to the
   *      ring. The ring says where it is; it cannot say that it is stuck there.
   *   2. Where it WAS set, an 14px glyph is the wrong instrument. A glyph says
   *      "look closer"; a blocked row needs to say WHAT TO DO, and the only
   *      thing that says that is words.
   *
   * So blocked renders as a LABEL (a tinted `IntentBadge`, per rule 10 — never a
   * hand-rolled span) plus a left edge bar on the row, so a list can be scanned
   * for "what is waiting on me" without reading a single title.
   *
   * Blocked OUTRANKS a plain notice: a row shows one or the other, never both,
   * because both answer the same question ("is anyone needed") and only one of
   * them is actionable right now.
   */
  blocked?: boolean;
}

/** 44px of date is always an abbreviation ("2h", "Jul 23"), and the abbreviation
 *  never says which date it is. `hint` does: "Due · Jul 23", "Waiting · 2h". */
export interface WorkDate {
  text: string;
  hint: string;
}

export interface WorkRowModel {
  id: string | number;
  signifier?: WorkSignifier;
  state?: WorkState;
  title: string;
  /** The one "needs a person" mark, sitting right after the title. */
  attention?: WorkAttention;
  /** Inline muted "why it surfaced", on the title's line. */
  subtitle?: string;
  meter?: WorkMeter;
  pills?: WorkPill[];
  owner?: WorkOwnerSlot;
  date?: WorkDate;
  actions?: WorkAction[];
  /** Navigate (peek / page). Takes precedence over `onOpen`. */
  href?: string;
  /** Open in place (drawer / dialog) — renders a button row. */
  onOpen?: () => void;
  /**
   * Inline content revealed directly beneath this row, inside the SAME list — a
   * verdict card, an expanded detail panel. `WorkList` renders it as the row's
   * SIBLING (never a child), so the WorkRow above stays the flex container's
   * `last:` child and keeps its divider. This is the seam that lets every
   * expandable list (the unified Issues queue's verdict cards) drop its
   * hand-rolled per-row wrapper — the wrapper that made each WorkRow the "last"
   * child of its own `<div>` and silently erased the row lines. Absent = nothing
   * revealed.
   */
  expansion?: React.ReactNode;
}

// ── Fixed slot metrics — the vertical rules ──────────────────────────────────
//
// The trailing band is a set of RESERVED COLUMNS, not a flex run. A row renders
// every column its LIST uses, empty when that row has no value for it — so the
// meter, the tags, the avatar, and the date each form a straight vertical rule
// down the list. Letting the band pack content-first is what made a goal's lane
// chip land at a different x than an issue's project chip, and a project's bar
// at a different x than a goal's (Matt, 2026-07-22: "we need these all to
// align").
//
// The two leading slots are both 16px, so the title's left edge is identical on
// every row regardless of which mark it carries.

const LEAD_SLOT = "inline-flex w-4 shrink-0 items-center justify-center";
const OWNER_SLOT = "inline-flex w-5 shrink-0 items-center justify-center";
const DATE_SLOT =
  "w-11 shrink-0 whitespace-nowrap text-right type-subtext tabular-nums text-muted-foreground";
// 128px: the bar (64px) + gap + a tally as wide as "812/949".
const METER_SLOT = "hidden w-32 shrink-0 items-center justify-end gap-2 md:flex";
// 112px: exactly ONE truncating chip.
//
// The column budget is the whole design constraint. Reserving every column for
// every list is what makes the rules straight, but the reserved band comes
// straight out of the title's measure — at a 640px content column an earlier
// 160px two-chip version left ~200px for the headline and every row read
// "Behind-mai…". So a row gets ONE tag; a second is a table, not a row. The
// ADAPTER picks which one is worth the slot at this scope (the project in a
// workspace-wide list, the section inside a project) — that judgement is
// exactly what the adapter layer is for.
const TAGS_SLOT = "hidden w-28 shrink-0 items-center justify-end sm:flex";

const AVATAR_COLORS = ["#4f46e5", "#0284c7", "#059669", "#d97706", "#e11d48", "#7c3aed"];
function ownerColor(label: string, explicit?: string): string {
  if (explicit) return explicit;
  let h = 0;
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}

// ── Slot renderers ───────────────────────────────────────────────────────────

function Signifier({ sig }: { sig?: WorkSignifier }) {
  if (!sig) return <span className={LEAD_SLOT} />;
  if (sig.as === "priority") {
    // Derived, never passed: the bars mean the same thing here as in the
    // picker, so they read from the same label map.
    return (
      <Tip label={<Dimension of="Priority">{PRIORITY_LABEL[sig.priority]}</Dimension>}>
        <span className={LEAD_SLOT} role="img" aria-label={`${PRIORITY_LABEL[sig.priority]} priority`}>
          <PriorityIcon priority={sig.priority} />
        </span>
      </Tip>
    );
  }
  // Muted, uncolored, always the same weight — the shape is the whole message,
  // and the bubble is what teaches the shape.
  const Glyph = sig.icon;
  return (
    <Tip label={sig.label}>
      <span className={LEAD_SLOT} role="img" aria-label={sig.label}>
        <Glyph className="size-3.5 text-muted-foreground" aria-hidden />
      </span>
    </Tip>
  );
}

function State({ state }: { state?: WorkState }) {
  if (!state) return <span className={LEAD_SLOT} />;
  // A stored status reads STATUS_LABEL (the same words the board and the picker
  // use); the two derived states have no entry there, so they fall through to
  // the ring's own map rather than to a hand-written second spelling.
  const label =
    state.as === "status"
      ? (state.label ?? STATUS_LABEL[state.status as TaskStatus] ?? ringLabel(state.status))
      : (state.label ?? PHASE_LABEL[state.phase]);
  return (
    <Tip label={<Dimension of="Status">{label}</Dimension>}>
      {/* No `role="img"`/`aria-label` here: `StatusRing` already renders an
          <svg role="img"> carrying this exact label, and nesting a second one
          made a screen reader announce the state twice ("Goal, Failed, Failed").
          The signifier slot below DOES label its span, correctly — its lucide
          glyph is `aria-hidden`, so the span is the only thing describing it. */}
      <span className={LEAD_SLOT}>
        {state.as === "status" ? (
          <StatusRing status={state.status} title={label} />
        ) : (
          <StatusRing
            status={PHASE_RING[state.phase]}
            intent={PHASE_INTENT[state.phase]}
            title={label}
          />
        )}
      </span>
    </Tip>
  );
}

function Meter({ meter }: { meter?: WorkMeter }) {
  if (!meter) return <span className={METER_SLOT} />;
  const pct = meter.max > 0 ? Math.round((meter.value / meter.max) * 100) : 0;
  // The bar is the one mark whose tooltip is strictly MORE than the row shows:
  // the row has a tally, the bubble spells out what is being tallied.
  const label =
    meter.max > 0 ? `${meter.value} of ${meter.max} ${meter.caption} · ${pct}%` : meter.caption;
  return (
    <Tip label={label}>
      <div className={cn("group/meter", METER_SLOT)} role="img" aria-label={label}>
        <span className="shrink-0 type-subtext tabular-nums text-muted-foreground">
          {meter.max > 0 ? (
            <>
              {/* The slash gets its own breathing room: "24/48" reads as one
                  six-glyph run, "24 / 48" reads as two numbers and a ratio. */}
              <span className="group-hover/meter:hidden">
                {meter.value}
                {/* `text-faint`, not `text-muted-foreground/50`: dimming a tier
                    with an alpha modifier is what the contrast ratchet forbids,
                    and the faint tier is the real token for "quieter than
                    muted". Invisible in the styleguide, which the contrast gate
                    doesn't scan — graduating this file into packages/ui is what
                    surfaced it. */}
                <span className="px-1 text-faint">/</span>
                {meter.max}
              </span>
              <span className="hidden group-hover/meter:inline">{pct}%</span>
            </>
          ) : null}
        </span>
        <span className="w-16 shrink-0">
          <HealthBar value={meter.value} max={meter.max} intent="neutral" />
        </span>
      </div>
    </Tip>
  );
}

/**
 * The attention mark — the ONE way any kind says "this needs a person, for a
 * reason that isn't its lifecycle state" *(Matt, 2026-07-22)*.
 *
 * A goal with no outcome and a review that's been sitting on you are the same
 * kind of fact, so they get the same mark: same glyph, same warning tone, same
 * position (immediately after the title, where the eye already is). What
 * differs is only the sentence in the bubble.
 *
 * It is deliberately NOT the state slot. State answers *where is this in its
 * life*; attention answers *is anyone needed*. A goal can be healthily in
 * progress AND missing its finish line, and folding those together loses the
 * second one. It is also NOT a reserved column — it costs zero width on the
 * rows that don't have it, which is most of them.
 */
function Attention({ attention }: { attention?: WorkAttention }) {
  if (!attention) return null;
  const level = attention.level ?? "warning";

  // BLOCKED — words, not a glyph. The chip carries its own label, so rule 7 is
  // satisfied without a tooltip: the rule is "no UNLABELED mark", and a mark
  // that spells itself out is the ideal case, not an exception. `IntentBadge`
  // rather than a tinted span, per rule 10.
  if (attention.blocked) {
    return (
      <IntentBadge intent={level} className="shrink-0 self-center whitespace-nowrap">
        {attention.label}
      </IntentBadge>
    );
  }

  return (
    <Tip label={<Dimension of="Needs attention">{attention.label}</Dimension>}>
      <span className="shrink-0 self-center" role="img" aria-label={`Needs attention: ${attention.label}`}>
        <CircleAlert
          className={cn("size-3.5", level === "danger" ? "text-danger" : "text-warning")}
          aria-hidden
        />
      </span>
    </Tip>
  );
}

/** Is this row stopped, waiting on the reader? Drives both the chip and the
 *  row's edge bar from ONE fact, so they can never disagree. */
function blockedTone(attention?: WorkAttention): "warning" | "danger" | null {
  if (!attention?.blocked) return null;
  return attention.level ?? "warning";
}

/** The single tag column — a goal's lane chip and an issue's project chip land
 *  on the same rule. Extra pills are dropped here rather than silently wrapping
 *  the row; the adapter is where the choice of which tag survives belongs. */
function Tags({ pills }: { pills?: WorkPill[] }) {
  if (!pills?.length) return <span className={TAGS_SLOT} />;
  return (
    <span className={TAGS_SLOT}>
      <WorkPillChip pill={pills[0]!} />
    </span>
  );
}

/** 112px of chip truncates, and the label alone never says what dimension it
 *  is — "Design" could be a project, a section, or a lane. The bubble carries
 *  both, which also makes it the fallback for the truncated text. */
export function WorkPillChip({ pill }: { pill: WorkPill }) {
  const Glyph = pill.icon;
  return (
    <Tip
      label={
        <Dimension of={pill.hint}>
          <span className={SENTENCE_CASE}>{pill.label}</span>
        </Dimension>
      }
    >
      {/* A FILLED chip, not an outline. `bg-background` is the page canvas —
          black under a dark card, a faint gray under a white one — so the tag
          reads as a recessed well rather than a hairline rectangle borrowing
          whatever is behind it. That last part is the real win: a transparent
          chip changed appearance on row hover and on the tinted group header;
          a filled one is the same object everywhere (Matt, 2026-07-22: "tags
          need to have the black background so we can see them easier"). */}
      <Badge variant="outline" className="min-w-0 bg-background text-muted-foreground">
        {Glyph ? (
          <Glyph className="size-2.5 shrink-0" style={pill.accent ? { color: pill.accent } : undefined} aria-hidden />
        ) : null}
        {/* Sentence case is the CHIP's job, not the data's — a tag reads as a
            label, and `micro`/`human`/`planner` came in lowercase from three
            different sources (Matt, 2026-07-22). */}
        <span className={cn("truncate", SENTENCE_CASE)}>{pill.label}</span>
      </Badge>
    </Tip>
  );
}

// ── The row ──────────────────────────────────────────────────────────────────

/**
 * Which trailing columns this LIST reserves. Alignment is a property of the
 * list, not of the row: a list where no row has a meter shouldn't hold 144px
 * open for one, but within a list every row must reserve the same columns or
 * the rules bend. `WorkList` derives this; a standalone `WorkRow` falls back to
 * "reserve exactly what I carry".
 */
export interface WorkColumns {
  meter: boolean;
  tags: boolean;
  owner: boolean;
  date: boolean;
}

export function columnsFor(rows: WorkRowModel[]): WorkColumns {
  return {
    meter: rows.some((r) => r.meter != null),
    tags: rows.some((r) => (r.pills?.length ?? 0) > 0),
    owner: rows.some((r) => r.owner != null),
    date: rows.some((r) => r.date != null),
  };
}

export interface WorkRowProps {
  row: WorkRowModel;
  /** Inset by the ONE card gutter (px-4). Bare lists under a Section eyebrow
   *  stay flush on the grid line. */
  gutter?: boolean;
  /** `default` = the --row-py rhythm (10px). `dense` = 6px, for embedded slices. */
  density?: "default" | "dense";
  /** Columns the parent list reserves. Defaults to this row's own content. */
  columns?: WorkColumns;
}

export function WorkRow({
  row,
  gutter = false,
  density = "default",
  columns,
}: WorkRowProps) {
  const { signifier, state, title, attention, subtitle, meter, pills, owner, date, actions, href, onOpen } =
    row;
  const cols = columns ?? columnsFor([row]);
  // See `isFailed`: a dead row's ring already says "wrong, and a person is
  // needed", with the reason in its bubble. The mark would repeat it in the same
  // color 8px later.
  const mark = isFailed(state) ? undefined : attention;
  const blocked = blockedTone(mark);

  const body = (
    <>
      {/* The edge bar. A blocked row is the one thing a reader must be able to
          find WITHOUT reading, and a 14px mark inside the row can't do that —
          the eye has to land on the row first to see it. A 2px rule on the
          card's own edge turns "what is waiting on me" into a vertical scan.
          This is not zebra (rule 1): it is not decoration or alternation, it is
          one semantic state marking itself, and it appears on a minority of rows
          by construction. */}
      {blocked ? (
        <span
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-y-0 left-0 w-0.5",
            blocked === "danger" ? "bg-danger" : "bg-warning",
          )}
        />
      ) : null}
      {/* Both leading slots ALWAYS render, empty when unused, so the title's
          left edge is identical on every row in the list. */}
      <Signifier sig={signifier} />
      <State state={state} />

      {/* Title (+ the inline muted "why"). The trailing cluster below is
          `shrink-0`, so it can never be shoved off by a long title — which
          means the title needs no hard width cap of its own and can use the
          whole remaining measure. LifecycleRow's `max-w-[50%]` predates the
          shrink-0 cluster and now just truncates early into a visible hole:
          on a row with only a date, the title stops at half and the other half
          is empty. The one cap that survives is on the title WHEN a subtitle
          is present, so the "why" always gets its share. */}
      <span className="flex min-w-0 flex-1 items-baseline gap-2">
        <span className={cn("truncate text-foreground", subtitle && "max-w-[60%] shrink-0")}>{title}</span>
        <Attention attention={mark} />
        {subtitle ? <span className="min-w-0 flex-1 truncate text-muted-foreground">{subtitle}</span> : null}
      </span>

      {/* The reserved trailing band. Each column the LIST uses is rendered by
          EVERY row — empty when this row has no value — so the meter, the tags,
          the avatar, and the date each fall on one vertical rule. The gaps are
          equal (10px) so no column reads as belonging to its neighbour. */}
      <div className="ml-auto flex shrink-0 items-center gap-2.5">
        {cols.meter ? <Meter meter={meter} /> : null}
        {cols.tags ? <Tags pills={pills} /> : null}
        {cols.owner ? (
          <span className={OWNER_SLOT}>
            {owner === "unassigned" ? (
              // The empty seat, not an empty cell. A dashed ring says "there
              // is a place for a person here and nobody is in it" — which a
              // blank 20px gap cannot say, because it looks exactly like a
              // kind that has no owner at all.
              <Tip label={<Dimension of="Assignee">Unassigned</Dimension>}>
                <span
                  role="img"
                  aria-label="Unassigned"
                  className="inline-flex size-5 items-center justify-center rounded-full border border-dashed border-muted-foreground/40"
                />
              </Tip>
            ) : owner ? (
              <Tip label={<Dimension of={owner.role ?? "Owner"}>{owner.name}</Dimension>}>
                <span
                  className="inline-flex size-5 items-center justify-center rounded-full type-subtext font-medium leading-none text-white"
                  style={{ backgroundColor: ownerColor(owner.initials, owner.color) }}
                >
                  {owner.initials}
                </span>
              </Tip>
            ) : null}
          </span>
        ) : null}
        {cols.date ? (
          <span className={DATE_SLOT}>
            {date ? (
              <Tip label={date.hint}>
                <span>{date.text}</span>
              </Tip>
            ) : null}
          </span>
        ) : null}
      </div>

      {/* Hover scrim + the decision cluster, anchored to the right edge. The
          scrim is translucent by design — it is hover feedback, not a mask, so
          the metadata under it stays legible. */}
      {actions?.length ? (
        <>
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-muted/70 via-muted/35 to-muted/0 opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100"
          />
          {/* `pointer-events-none` on the CONTAINER, `auto` on the buttons: the
              cluster's pl-3 and the gaps between buttons are invisible, so they
              must not swallow the pointer. They used to — the 12px pad landed
              exactly on the owner avatar, which meant hovering an avatar on a
              row with actions silently did nothing. Harmless while nothing under
              there responded to hover; a broken promise now that every mark
              carries a tooltip. */}
          <div
            className={cn(
              "pointer-events-none absolute inset-y-0 z-10 flex items-center gap-1 pl-3 opacity-0 transition-opacity group-hover/row:opacity-100 focus-within:opacity-100",
              gutter ? "right-4" : "right-0",
            )}
          >
            {actions.map((a, i) => (
              <Tip key={i} label={a.label}>
                <Button
                  variant="outline"
                  size="icon-sm"
                  className="pointer-events-auto"
                  aria-label={a.label}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    a.onClick?.();
                  }}
                >
                  <a.icon />
                </Button>
              </Tip>
            ))}
          </div>
        </>
      ) : null}
    </>
  );

  const className = cn(
    // Full-strength `border-border` divider (the same alpha hairline the Card
    // atom uses), not the faint `/40` the first cut shipped: rows read as
    // discrete items — the "row lines" — instead of dissolving into the card
    // (Matt, 2026-07-23: "we lost the row lines … rows need some kind of color
    // to them like cards").
    "group/row relative flex w-full items-center gap-3 border-b border-border text-left type-body last:border-b-0",
    density === "dense" ? "py-1.5" : "py-[var(--row-py)]",
    gutter && "px-[var(--content-gutter)]",
    (href || onOpen) && "cursor-pointer transition-colors hover:bg-secondary",
  );

  const element = href ? (
    // `MaybeObjectLink`, not a raw `next/link`: an href that points at a
    // canonical object page (`/o/<uid>`) opens the object's PEEK in place —
    // the "prefer the peek" doorway every object surface uses — while an
    // ordinary href (`/issues?task=…`) stays a plain navigation. Without this a
    // WorkRow whose adapter passes `objectHref(uid)` (the hiring rows, goals)
    // would full-page-navigate where the row it replaced opened the modal.
    <MaybeObjectLink href={href} className={className}>
      {body}
    </MaybeObjectLink>
  ) : onOpen ? (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
      className={className}
    >
      {body}
    </div>
  ) : (
    <div className={className}>{body}</div>
  );

  // ONE provider per row. It renders no DOM — it is pure context — so this
  // neither changes the list's layout nor costs an element per mark, and a row
  // dropped anywhere (a card, a popover, a bare page) still gets its bubbles.
  //
  // `disableHoverableContent`: the bubble closes the moment the pointer leaves
  // the mark, instead of staying alive while you're over the bubble itself
  // (Matt, 2026-07-22). Hoverable content exists so you can reach a link inside
  // a tooltip; ours are three words and hold nothing to reach, so all that
  // behavior buys is a bubble that follows you around and covers the row above.
  // It also retires the grace-area transit rule, so two adjacent marks now open
  // one after the other instead of the second swallowing your first move.
  return (
    <TooltipProvider delayDuration={300} disableHoverableContent>
      {element}
    </TooltipProvider>
  );
}

// ── The list ─────────────────────────────────────────────────────────────────

export interface WorkListProps {
  rows: WorkRowModel[];
  gutter?: boolean;
  density?: "default" | "dense";
  empty?: React.ReactNode;
  /** An optional muted glyph above the empty message, for a fuller blank state. */
  emptyIcon?: React.ReactNode;
  /**
   * Force a column set instead of deriving it. Pass when several lists sit on
   * one screen and must share their rules — the Work dashboard's stacked
   * sections, say — so a goals list and a projects list put their meters on the
   * same x even though each derives a different set alone.
   */
  columns?: WorkColumns;
}

export function WorkList({
  rows,
  gutter,
  density,
  columns,
  empty = "Nothing here right now.",
  emptyIcon,
}: WorkListProps) {
  // Derived ONCE for the list, then handed to every row — that is what makes
  // the trailing rules straight.
  const cols = columns ?? columnsFor(rows);
  if (rows.length === 0) {
    // A PROPER blank state, not a thin left-aligned line: centered, with room to
    // breathe (and an optional glyph), so an expanded-but-empty group reads as
    // "nothing here yet" on purpose rather than a rendering gap (Matt,
    // 2026-07-23: "if we open it, we need a proper blank state there").
    return (
      <div
        data-slot="work-list-empty"
        className={cn(
          "flex flex-col items-center justify-center gap-2 py-10 text-center type-body text-muted-foreground",
          gutter ? "px-4" : "px-6",
        )}
      >
        {emptyIcon ? (
          <div className="text-muted-foreground" aria-hidden>
            {emptyIcon}
          </div>
        ) : null}
        <div>{empty}</div>
      </div>
    );
  }
  return (
    <div className="flex flex-col" data-slot="work-list">
      {rows.map((row) => (
        <React.Fragment key={row.id}>
          <WorkRow row={row} gutter={gutter} density={density} columns={cols} />
          {row.expansion != null ? (
            // The row's SIBLING (a Fragment is transparent), so the WorkRow above
            // stays the flex container's `last:` child and keeps its divider. A
            // per-row wrapper `<div>`/`<li>` here — which is what every list that
            // needed an expansion used to reach for — made each WorkRow the "last"
            // child of its own wrapper, so `last:border-b-0` matched every row and
            // erased the row lines list-wide.
            <div className={cn("pb-3", gutter && "px-4")}>{row.expansion}</div>
          ) : null}
        </React.Fragment>
      ))}
    </div>
  );
}
